import axios from 'axios'
import {useEffect, useRef, useState} from 'react'
import {createCancelSource, globalSearch} from '../../services/searchService'
import {getExperts, PublicExpert} from '../../services/expertsService'
import {ExpertiseResultItem, GlobalSearchResponse, SearchType} from '../../pages/search/types'
import {meetsMinLength} from '../../pages/search/utils'

interface GlobalSearchHookState {
  data: GlobalSearchResponse | null
  isFetching: boolean
  error: Error | null
}

// PR-I-82: el aggregator de backend todavia devuelve ExpertiseService en el bucket `expertise`
// (title de servicio, no nombre de partner). Mientras se mergea el fix backend, sustituimos el
// bucket con Partners tipo Expert usando el mismo endpoint que consume /experts. Asi el
// dropdown y /search muestran nombre + address + count + logo del partner.
const overrideExpertBucket = (
  base: GlobalSearchResponse,
  experts: PublicExpert[],
): GlobalSearchResponse => {
  const items: ExpertiseResultItem[] = experts.map((e) => ({
    type: 'expertise',
    id: e.id,
    companyName: e.company_name ?? '',
    address: e.address ?? '',
    logo: e.logo ?? '',
    expertiseCount: e.expertise_count ?? 0,
  }))
  return {
    ...base,
    buckets: {
      ...base.buckets,
      expertise: {total: items.length, items},
    },
  }
}

/**
 * Fires the aggregate search for (query, type). Follows the repo's mounted-ref hook pattern.
 *
 * - Does nothing until the trimmed query is >= 2 chars (contract: shorter → empty, no request);
 *   below the threshold it clears data/error so a stale dropdown never lingers.
 * - Cancels the in-flight request (CancelToken) whenever query or type changes, or on unmount.
 * - Never sets state after unmount.
 *
 * The caller is expected to pass an already-debounced query (see useDebouncedValue).
 */
export function useGlobalSearch(query: string, type: SearchType): GlobalSearchHookState {
  const [data, setData] = useState<GlobalSearchResponse | null>(null)
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  useEffect(() => {
    const q = (query ?? '').trim()

    if (!meetsMinLength(q)) {
      setData(null)
      setError(null)
      setIsFetching(false)
      return
    }

    const source = createCancelSource()
    setIsFetching(true)
    setError(null)

    // Solo pedimos experts cuando el bucket expertise es visible (scope=all o scope=expertise).
    // Cuando el usuario esta filtrando members/experiences/forums no gastamos una llamada extra.
    const needsExperts = type === 'all' || type === 'expertise'

    Promise.all([
      globalSearch(q, type, undefined, undefined, {cancelToken: source.token}),
      needsExperts ? getExperts(q, '') : Promise.resolve<PublicExpert[]>([]),
    ])
      .then(([aggregate, experts]) => {
        if (!isMountedRef.current) return
        const res = needsExperts ? overrideExpertBucket(aggregate, experts) : aggregate
        setData(res)
        setIsFetching(false)
      })
      .catch((err) => {
        if (axios.isCancel(err)) return // superseded — a newer query is in flight
        if (!isMountedRef.current) return
        setError(err as Error)
        setIsFetching(false)
      })

    return () => source.cancel('search superseded')
  }, [query, type])

  return {data, isFetching, error}
}
